import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Orb } from "@/components/earnos/Orb";

import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Engine from "@/pages/Engine";
import Opportunities from "@/pages/Opportunities";
import Money from "@/pages/Money";
import You from "@/pages/You";
import Moves from "@/pages/Moves";
import MoveDetail from "@/pages/MoveDetail";
import Plan from "@/pages/Plan";
import OfferBuilder from "@/pages/OfferBuilder";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Earnings from "@/pages/Earnings";
import IncomeAssets from "@/pages/IncomeAssets";
import Portfolio from "@/pages/Portfolio";
import Reputation from "@/pages/Reputation";
import Settings from "@/pages/Settings";
import Autopilot from "@/pages/Autopilot";
import Billing from "@/pages/Billing";
import BillingReturn from "@/pages/BillingReturn";
import ConnectReturn from "@/pages/ConnectReturn";
import Admin from "@/pages/Admin";
import { RealityCheck, EconomicGraph } from "@/pages/EconomicTools";
import TrustCenter from "@/pages/TrustCenter";
import Legal from "@/pages/Legal";
import MissionControl from "@/pages/MissionControl";
import EarnosChat from "@/pages/EarnosChat";
import Connections from "@/pages/Connections";
import BrowserAccess from "@/pages/BrowserAccess";

function RootRedirect() {
  const { user, loading, authError, retrySession } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <Orb state="thinking" size={120} />
      </div>
    );
  }
  if (authError) return <div role="alert" className="min-h-screen bg-surface text-ink flex flex-col items-center justify-center gap-4 p-6"><p>{authError}</p><button type="button" onClick={retrySession} className="underline">Retry connection</button></div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <Navigate to="/home" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="dark" position="top-center" richColors closeButton />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/legal/:topic" element={<Legal />} />
          <Route path="/onboarding" element={<ProtectedRoute requireOnboarded={false}><Onboarding /></ProtectedRoute>} />
          <Route path="/billing-return" element={<ProtectedRoute requireOnboarded={false}><BillingReturn /></ProtectedRoute>} />
          <Route path="/connect-return" element={<ProtectedRoute requireOnboarded={false}><ConnectReturn /></ProtectedRoute>} />

          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/home" element={<Home />} />
            <Route path="/engine" element={<Engine />} />
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/money" element={<Money />} />
            <Route path="/you" element={<You />} />
            <Route path="/moves" element={<Moves />} />
            <Route path="/move/:id" element={<MoveDetail />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/offer-builder" element={<OfferBuilder />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/lead/:id" element={<LeadDetail />} />
            <Route path="/earnings" element={<Earnings />} />
            <Route path="/income-assets" element={<IncomeAssets />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/reputation" element={<Reputation />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/autopilot" element={<Autopilot />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/reality-check" element={<RealityCheck />} />
            <Route path="/economic-graph" element={<EconomicGraph />} />
            <Route path="/trust-center" element={<TrustCenter />} />
            <Route path="/mission" element={<MissionControl />} />
            <Route path="/chat" element={<EarnosChat />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/browser-access" element={<BrowserAccess />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
